<template>
  <div class="payslip-items-table-scroll">
    <div v-if="loading" class="payslip-items-table-loader">
      <oxd-loading-spinner />
    </div>
    <table v-else class="payslip-items-table">
      <colgroup>
        <col class="payslip-items-table__col-name" />
        <col class="payslip-items-table__col-amount" />
        <col class="payslip-items-table__col-description" />
      </colgroup>
      <thead>
        <tr>
          <th
            v-for="(header, colIndex) in headers"
            :key="header.name"
            :class="headerCellClass(colIndex)"
          >
            {{ header.title }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="!items.length">
          <td :colspan="headers.length" class="payslip-items-table__empty">
            {{ emptyMessage }}
          </td>
        </tr>
        <tr v-for="(row, rowIndex) in items" :key="row.id ?? rowIndex">
          <td
            v-for="(header, colIndex) in headers"
            :key="header.name"
            :class="bodyCellClass(colIndex, header.name)"
          >
            <truncated-note-cell
              v-if="header.name === 'description'"
              :text="row.description || ''"
            />
            <span v-else>{{ formatCell(row, header.name) }}</span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import {h} from 'vue';
import {OxdSpinner} from '@ohrm/oxd';

const TruncatedNoteCell = {
  name: 'TruncatedNoteCell',
  props: {
    text: {
      type: String,
      default: '',
    },
  },
  computed: {
    displayText() {
      if (!this.text || this.text.length <= 40) {
        return this.text || '';
      }
      return `${this.text.slice(0, 40)}...`;
    },
  },
  render() {
    return h(
      'span',
      {
        class: 'payslip-items-table__description',
        title: this.text || '',
      },
      this.displayText,
    );
  },
};

const numericColumnNames = new Set(['amount']);
const textColumnNames = new Set(['itemName', 'description']);

export default {
  name: 'PayslipItemsTable',
  components: {
    'oxd-loading-spinner': OxdSpinner,
    'truncated-note-cell': TruncatedNoteCell,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    emptyMessage: {
      type: String,
      default: '',
    },
  },
  computed: {
    headers() {
      return [
        {name: 'itemName', title: this.$t('general.name')},
        {name: 'amount', title: this.$t('general.amount')},
        {name: 'description', title: this.$t('general.description')},
      ];
    },
  },
  methods: {
    formatCell(row, field) {
      const value = row[field];
      if (value === null || value === undefined) {
        return '';
      }
      return String(value);
    },
    headerCellClass(colIndex) {
      const name = this.headers[colIndex]?.name ?? '';
      return {
        'payslip-items-table__cell--text': textColumnNames.has(name),
        'payslip-items-table__cell--numeric': numericColumnNames.has(name),
        'payslip-items-table__cell--description': name === 'description',
      };
    },
    bodyCellClass(_colIndex, fieldName) {
      return {
        'payslip-items-table__cell--text': textColumnNames.has(fieldName),
        'payslip-items-table__cell--numeric': numericColumnNames.has(fieldName),
        'payslip-items-table__cell--description': fieldName === 'description',
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.payslip-items-table-scroll {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #9ca3af transparent;
  border: 1px solid $oxd-interface-gray-lighten-2-color;
  border-radius: 4px;
  box-sizing: border-box;
}

.payslip-items-table-scroll::-webkit-scrollbar {
  height: 8px;
}

.payslip-items-table-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.payslip-items-table-scroll::-webkit-scrollbar-thumb {
  background-color: #9ca3af;
  border-radius: 4px;
}

.payslip-items-table-loader {
  display: flex;
  justify-content: center;
  padding: 1.5rem;
}

.payslip-items-table {
  width: 100%;
  min-width: 520px;
  border-collapse: collapse;
  table-layout: fixed;
  background-color: $oxd-white-color;
  font-family: sans-serif;
  font-size: 12px;
  color: $oxd-interface-gray-darken-1-color;

  th,
  td {
    padding: 0.65rem 0.75rem;
    border-top: 1px solid $oxd-interface-gray-lighten-2-color;
    vertical-align: middle;
  }

  thead th {
    font-weight: 700;
    background-color: $oxd-background-light-gray-color;
    border-top: 0;
    white-space: nowrap;
  }

  tbody tr:hover {
    background-color: $oxd-background-light-gray-color;
  }

  &__empty {
    text-align: center;
    color: $oxd-interface-gray-darken-1-color;
    font-style: italic;
    padding: 1.25rem;
  }

  &__cell--text {
    text-align: left;
  }

  &__cell--numeric {
    text-align: right;
    white-space: nowrap;
  }

  &__cell--description {
    text-align: left;
    white-space: normal;
    word-break: break-word;
  }

  &__col-name {
    width: 24%;
  }

  &__col-amount {
    width: 22%;
  }

  &__col-description {
    width: 54%;
  }
}

::v-deep(.payslip-items-table__description) {
  cursor: default;
  word-break: break-word;
  text-align: left;
  display: inline-block;
  width: 100%;
}
</style>
